import React, { useState, useMemo, useEffect, useRef } from 'react'
import {
    Autocomplete,
    Button,
    Box,
    Typography,
    TextField,
    Chip,
    Grid,
    FormControl,
    Select,
    OutlinedInput,
    MenuItem,
    Checkbox,
    ListItemText,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControlLabel,
    Pagination,
    Tooltip,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SearchModal from '../../shared/components/SearchModal'
import ActivityResultCard, { ActivityResult } from './ActivityResultCard'
import OverwriteConfirmDialog from './OverwriteConfirmDialog'
import { useGetActivitiesQuery } from '../../shared/queries/useGetActivitiesQuery'
import { useGetOrgActivitiesQuery } from '../../shared/queries/useGetOrgActivitiesQuery'
import {
    useSearchExternalActivitiesQuery,
    ExternalActivitySearchParams,
} from '../../shared/queries/useSearchExternalActivitiesQuery'
import { useGetExternalActivityFacetsQuery } from '../../shared/queries/useGetExternalActivityFacetsQuery'
import { useGetFieldMappingsQuery, FieldMappingField } from '../../shared/queries/useGetFieldMappingsQuery'
import { useImportActivitiesMutation } from '../../shared/queries/useImportActivitiesMutation'
import { useImportExternalActivityMutation } from '../../shared/queries/useImportExternalActivityMutation'
import { useInvalidateActivitiesQuery } from '../../shared/queries/useInvalidateActivitiesQuery'
import { useStrategy } from '../../shared/hooks/useStrategy'
import useActivityStatuses from '../../shared/hooks/useActivityStatuses'
import { normalizeActivityStatus } from '../../shared/hooks/useActivityStatuses/useActivityStatuses.helpers'
import { getISOStringFromCalendarDate } from '../../utilities/helpers'
import dayjs from 'dayjs'
import { EmptyPlaceholder } from '../../shared/components/EmptyPlaceholder'
import {
    INTEGRATIONS,
    IntegrationCode,
    isIntegrationEnabled,
    integrationCodeToSourceString,
    sourceStringToIntegrationCode,
    SOURCE_CHIP_SX,
} from '../../shared/constants/integrations'
import { useGetOrganizationQuery } from '../../shared/queries/useGetOrganizationQuery'

interface ActivitySearchFormState {
    statuses: string[]
    studyTitle: string
    studyTypes: string[]
    therapeuticArea: string
    genericNames: string[]
    sources: string[]
    compoundNames: string[]
    indications: string[]
    studyId: string
    hideImported: boolean
}

const INITIAL_FORM: ActivitySearchFormState = {
    statuses: [],
    studyTitle: '',
    studyTypes: [],
    therapeuticArea: '',
    genericNames: [],
    sources: [],
    compoundNames: [],
    indications: [],
    studyId: '',
    hideImported: false,
}

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50]

interface ActivitySearchModalProps {
    trigger?: (handleOpen: () => void) => React.ReactNode
    disabled?: boolean
    onImportSuccess?: () => void
}

const toActivityResult = (
    activity: any,
    activityStatusDetails: Array<{ id: string; name: string; color?: string }>,
    currentRoadmapIds: Set<string>
): ActivityResult => {
    const statusDetails = activityStatusDetails.find((s) => s.id === activity.status)
    return {
        _id: activity._id,
        title: activity.title,
        titleLong: activity.titleLong,
        studyNumber: activity.studyNumber,
        category: activity.category,
        function: activity.function,
        indication: activity.indication,
        genericName: activity.genericName,
        therapeuticArea: activity.therapeuticArea,
        compoundName: activity.compoundName,
        source: activity.source ?? 'Atlas',
        status: activity.status,
        description: activity.description,
        startDate: activity.startDate,
        endDate: activity.endDate,
        plannedStartDate: activity.plannedStartDate,
        plannedEndDate: activity.plannedEndDate,
        affiliateName: activity.affiliateName,
        providerName: activity.providerName,
        sponsorType: activity.sponsorType,
        countries: activity.countries,
        studyReportDate: activity.studyReportDate,
        terminationDate: activity.terminationDate,
        studyOwner: activity.studyOwner,
        vendorDetails: activity.vendorDetails,
        vendor: activity.vendor,
        type: activity.type,
        population: activity.population,
        externalId: activity.externalId,
        lastSyncedAt: activity.lastSyncedAt,
        updatedAt: activity.updatedAt,
        chipColor: (() => {
            const code = sourceStringToIntegrationCode(activity.source ?? 'Atlas')
            return code ? INTEGRATIONS[code].chipColor : undefined
        })(),
        statusColor: statusDetails?.color,
        isImported: currentRoadmapIds.has(activity._id),
        roadmapNames: activity?.roadmapNames ?? [],
    }
}

const ActivitySearchModal: React.FC<ActivitySearchModalProps> = ({ trigger, disabled, onImportSuccess }) => {
    const [open, setOpen] = useState(false)
    const [selectedActivities, setSelectedActivities] = useState<Record<string, ActivityResult>>({})
    // Titles of already-imported external records that a pending import would overwrite. Non-empty
    // opens the overwrite-confirmation dialog; the import is held until the user confirms.
    const [overwriteTitles, setOverwriteTitles] = useState<string[]>([])
    const [hasSearched, setHasSearched] = useState(false)
    const [page, setPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(25)
    const [sortBy, setSortBy] = useState<'default' | 'title_asc' | 'title_desc' | 'recently_updated'>('default')
    const leftPanelRef = useRef<HTMLDivElement>(null)

    const strategy = useStrategy()
    // Activities can't cross a sandbox roadmap's boundary (enforced server-side too), so importing is off.
    const isSandbox = Boolean(strategy?.isSandbox)
    const { data: fieldMappings } = useGetFieldMappingsQuery(strategy?.organizationId ?? '')
    const { data: orgActivities, isFetching: orgLoading } = useGetOrgActivitiesQuery()
    const { data: currentRoadmapActivities } = useGetActivitiesQuery()
    const { data: organization } = useGetOrganizationQuery()
    const { mutateAsync: importActivities, isLoading: isImportingInternal } = useImportActivitiesMutation()
    const { mutateAsync: importExternalActivity, isLoading: isImportingExternal } = useImportExternalActivityMutation()
    const { invalidateActivitiesQuery } = useInvalidateActivitiesQuery()
    const isImporting = isImportingInternal || isImportingExternal
    const { activityStatusDetails } = useActivityStatuses()

    const availableIntegrations = useMemo(
        () => Object.values(INTEGRATIONS).filter((i) => isIntegrationEnabled(organization?.integrations, i.code)),
        [organization?.integrations]
    )
    const enabledSourceCodes = useMemo(() => availableIntegrations.map((i) => i.code), [availableIntegrations])
    const allStatusIds = useMemo(() => activityStatusDetails.map((s) => s.id), [activityStatusDetails])

    // When the user has every option in a multi-select checked, treat it as
    // "no filter" — otherwise records whose value is null/legacy/unknown get
    // dropped by an explicit IN list even though the UI says "All".
    const isAllSelected = (selected: string[], all: string[]) =>
        all.length > 0 && all.every((id) => selected.includes(id))

    const buildInitialForm = (): ActivitySearchFormState => ({
        ...INITIAL_FORM,
        sources: enabledSourceCodes,
        statuses: allStatusIds,
    })

    const [form, setForm] = useState<ActivitySearchFormState>(buildInitialForm)
    const [appliedForm, setAppliedForm] = useState<ActivitySearchFormState>(buildInitialForm)

    const { data: facetsData } = useGetExternalActivityFacetsQuery()

    // Keep sources + statuses in sync with their full enabled sets until the user runs their first search.
    // Handles the case where the org / statuses queries resolve after the initial render.
    useEffect(() => {
        if (hasSearched) return
        setForm((f) => ({ ...f, sources: enabledSourceCodes, statuses: allStatusIds }))
        setAppliedForm((f) => ({ ...f, sources: enabledSourceCodes, statuses: allStatusIds }))
    }, [enabledSourceCodes, allStatusIds, hasSearched])

    // Pre-populate indication and generic name from the roadmap once facets are available.
    // Only pre-fills the form (visible state) — the user still has to click Search to apply.
    // Skips if the roadmap value isn't in the facets list (avoids pre-selecting something with no results).
    useEffect(() => {
        if (hasSearched || !facetsData?.indications) return
        const preIndications =
            strategy?.indication && facetsData.indications.includes(strategy.indication)
                ? [strategy.indication]
                : INITIAL_FORM.indications
        const preGenericNames =
            strategy?.product && facetsData.genericNames?.includes(strategy.product)
                ? [strategy.product]
                : INITIAL_FORM.genericNames
        setForm((f) => ({ ...f, indications: preIndications, genericNames: preGenericNames }))
    }, [facetsData, hasSearched])

    // Returns the synonyms for a given field + value if an exact mapping exists, otherwise null.
    const getSynonymsFor = (field: FieldMappingField, value: string): string[] | null => {
        if (!fieldMappings || !value) return null
        const mapping = fieldMappings.find(
            (m) => m.field === field && m.primaryValue.toLowerCase() === value.toLowerCase()
        )
        return mapping ? mapping.synonyms : null
    }

    // Returns [primaryValue, ...synonyms] if the value exactly matches a primaryValue in the mappings
    // for the given field; otherwise returns [value] unchanged.
    const expandMappedValues = (field: FieldMappingField, values: string[]): string[] => {
        if (!fieldMappings || values.length === 0) return values
        const expanded = new Set<string>()
        values.forEach((v) => {
            expanded.add(v)
            const mapping = fieldMappings.find(
                (m) => m.field === field && m.primaryValue.toLowerCase() === v.toLowerCase()
            )
            if (mapping) {
                mapping.synonyms.forEach((s) => expanded.add(s))
            }
        })
        return Array.from(expanded)
    }

    const currentRoadmapIds = useMemo(
        () => new Set((currentRoadmapActivities ?? []).map((a) => a._id)),
        [currentRoadmapActivities]
    )

    // Study numbers for activities already imported onto THIS roadmap. Sent to the search connector
    // as `excludeStudyNumbers` so already-imported records are filtered out server-side (before
    // pagination), reproducing the old "Hide already imported" behavior without client-side filtering.
    // Study number is globally unique in the source, so matching on it alone is unambiguous.
    const importedStudyNumbers = useMemo(
        () =>
            (orgActivities ?? [])
                .map((a) => (currentRoadmapIds.has(a._id) ? (a.studyNumber ?? '').trim() : ''))
                .filter((n) => n !== ''),
        [orgActivities, currentRoadmapIds]
    )

    const searchParams: ExternalActivitySearchParams = useMemo(() => {
        const taValues = appliedForm.therapeuticArea
            ? expandMappedValues('therapeuticArea', [appliedForm.therapeuticArea])
            : undefined
        const gnValues = appliedForm.genericNames.length
            ? expandMappedValues('genericName', appliedForm.genericNames)
            : undefined
        const cnValues = appliedForm.compoundNames.length
            ? expandMappedValues('compoundName', appliedForm.compoundNames)
            : undefined
        const indValues = appliedForm.indications.length
            ? expandMappedValues('indication', appliedForm.indications)
            : undefined
        const stValues = appliedForm.studyTypes.length
            ? expandMappedValues('studyType', appliedForm.studyTypes)
            : undefined

        // If expansion produced more than one value, use the array form (exact IN match).
        // Otherwise fall back to the single-string LIKE match for partial text queries.
        const taExpanded = taValues && taValues.length > 1

        return {
            statuses:
                isAllSelected(appliedForm.statuses, allStatusIds) || appliedForm.statuses.length === 0
                    ? undefined
                    : appliedForm.statuses,
            studyTitle: appliedForm.studyTitle || undefined,
            types: stValues?.length ? stValues : undefined,
            therapeuticAreas: taExpanded ? taValues : undefined,
            therapeuticArea: !taExpanded ? appliedForm.therapeuticArea || undefined : undefined,
            genericNames: gnValues,
            sources: (() => {
                if (isAllSelected(appliedForm.sources, enabledSourceCodes)) return undefined
                const enabledSet = new Set(enabledSourceCodes)
                const codes = appliedForm.sources.filter((s): s is IntegrationCode =>
                    enabledSet.has(s as IntegrationCode)
                )
                if (!codes.length) return undefined
                return codes.map((code) => integrationCodeToSourceString(code))
            })(),
            compoundNames: cnValues,
            indications: indValues,
            studyId: appliedForm.studyId || undefined,
            // "Hide already imported" is a pure search modifier: when set, the roadmap's imported
            // study numbers are handed to the connector, which excludes them before paginating.
            // Empty list → omit the param so behavior is unchanged.
            excludeStudyNumbers:
                appliedForm.hideImported && importedStudyNumbers.length ? importedStudyNumbers : undefined,
            limit: rowsPerPage,
            offset: (page - 1) * rowsPerPage,
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedForm, page, rowsPerPage, fieldMappings, importedStudyNumbers])

    const {
        data: searchData,
        isFetching: isSearchLoading,
        isError: searchError,
        refetch: refetchSearch,
    } = useSearchExternalActivitiesQuery(searchParams, hasSearched)

    const isLoading = orgLoading || isSearchLoading

    const uniqueIndications = facetsData?.indications ?? []

    const handleOpen = () => setOpen(true)
    const handleClose = () => {
        setOpen(false)
        setSelectedActivities({})
        setForm(buildInitialForm())
        setAppliedForm(buildInitialForm())
        setHasSearched(false)
        setPage(1)
        setSortBy('default')
    }

    const handleSearch = () => {
        setAppliedForm({ ...form })
        setHasSearched(true)
        setPage(1)
        leftPanelRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
        // Always hit the network on a manual Search click. If the search params
        // haven't changed (e.g. user clicks Search twice), React Query would
        // otherwise return cached data within staleTime and skip the request.
        refetchSearch()
    }

    // Reproduces the prior client-side substring/multi-select matcher, applied to org-DB activities only.
    // The Snowflake-sourced rows are already filtered server-side and arrive pre-matched.
    const filteredOrgActivities = useMemo((): ActivityResult[] => {
        if (!hasSearched || !orgActivities) return []

        // Expand indications and study types through mappings for client-side filtering too
        const expandedIndications = appliedForm.indications.length
            ? expandMappedValues('indication', appliedForm.indications)
            : []
        const expandedStudyTypes = appliedForm.studyTypes.length
            ? expandMappedValues('studyType', appliedForm.studyTypes)
            : []
        const expandedTherapeuticAreas = appliedForm.therapeuticArea
            ? expandMappedValues('therapeuticArea', [appliedForm.therapeuticArea])
            : []
        const expandedGenericNames = appliedForm.genericNames.length
            ? expandMappedValues('genericName', appliedForm.genericNames)
            : []
        const expandedCompoundNames = appliedForm.compoundNames.length
            ? expandMappedValues('compoundName', appliedForm.compoundNames)
            : []

        return orgActivities
            .filter((activity) => {
                const matchesStatus =
                    appliedForm.statuses.length === 0 ||
                    isAllSelected(appliedForm.statuses, allStatusIds) ||
                    appliedForm.statuses.includes(activity.status)
                const matchesTitle =
                    !appliedForm.studyTitle ||
                    activity.title?.toLowerCase().includes(appliedForm.studyTitle.toLowerCase())
                const matchesType = expandedStudyTypes.length === 0 || expandedStudyTypes.includes(activity.type)
                const matchesTherapeuticArea =
                    expandedTherapeuticAreas.length === 0 ||
                    (expandedTherapeuticAreas.length > 1
                        ? // Expanded via mapping — use exact match against the expanded set
                          expandedTherapeuticAreas.some(
                              (ta) => ta.toLowerCase() === activity.therapeuticArea?.toLowerCase()
                          )
                        : // No mapping match — fall back to substring
                          activity.therapeuticArea?.toLowerCase().includes(appliedForm.therapeuticArea.toLowerCase()))
                const matchesGenericName =
                    expandedGenericNames.length === 0 ||
                    [activity.genericName]
                        .flat()
                        .some((n: string) => expandedGenericNames.some((gn) => gn.toLowerCase() === n?.toLowerCase()))
                const activitySourceCode = sourceStringToIntegrationCode(activity.source ?? 'Atlas')
                const matchesSource =
                    appliedForm.sources.length === 0 ||
                    isAllSelected(appliedForm.sources, enabledSourceCodes) ||
                    (activitySourceCode !== null && appliedForm.sources.includes(activitySourceCode))
                const matchesCompoundName =
                    expandedCompoundNames.length === 0 ||
                    [activity.compoundName]
                        .flat()
                        .some((n: string) => expandedCompoundNames.some((cn) => cn.toLowerCase() === n?.toLowerCase()))
                const matchesIndication =
                    expandedIndications.length === 0 || expandedIndications.includes(activity.indication)
                const matchesStudyId =
                    !appliedForm.studyId ||
                    activity.studyNumber?.toLowerCase().includes(appliedForm.studyId.toLowerCase())
                // "Hide already imported" must apply to org-DB rows too. The server side only
                // excludes imported study numbers from its results/total; if we don't mirror that
                // here, an activity already on this roadmap still renders as a card while being
                // absent from the count — cards and "N results" drift apart.
                const matchesHideImported = !appliedForm.hideImported || !currentRoadmapIds.has(activity._id)

                return (
                    matchesStatus &&
                    matchesTitle &&
                    matchesType &&
                    matchesTherapeuticArea &&
                    matchesGenericName &&
                    matchesSource &&
                    matchesCompoundName &&
                    matchesIndication &&
                    matchesStudyId &&
                    matchesHideImported
                )
            })
            .map((activity): ActivityResult => toActivityResult(activity, activityStatusDetails, currentRoadmapIds))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasSearched, orgActivities, appliedForm, activityStatusDetails, currentRoadmapIds, fieldMappings])

    // Org activities surface on page 1 above the server page; subsequent pages show only server results.
    // Planisware records already on this roadmap are pushed to the bottom of the external block —
    // the sync job keeps them current, so manual re-import (which now overwrites) is rarely needed.
    const paginatedActivities = useMemo((): ActivityResult[] => {
        if (!hasSearched) return []
        const serverResults = (searchData?.results ?? []).map((a) =>
            toActivityResult(a, activityStatusDetails, currentRoadmapIds)
        )

        // A GSL/Planisware study already imported into Atlas surfaces twice — once as an org
        // (Atlas DB) row with roadmapNames, and once as a fresh external result with a different
        // _id. The backend's canonical identity is studyNumber + source, so merge on that key:
        // keep the org row (preserves ordering + roadmap context) but graft the fresh external
        // field values onto it, and flag it inAtlas so the card renders both source chips.
        const keyOf = (a: ActivityResult): string | null =>
            a.studyNumber ? `${(a.source ?? '').toLowerCase().trim()}|${a.studyNumber.toLowerCase().trim()}` : null

        const serverByKey = new Map<string, ActivityResult>()
        serverResults.forEach((a) => {
            const k = keyOf(a)
            if (k) serverByKey.set(k, a)
        })

        const orgResults: ActivityResult[] = filteredOrgActivities.map((a) => {
            const k = keyOf(a)
            const fresh = k ? serverByKey.get(k) : undefined
            return fresh
                ? { ...fresh, _id: a._id, roadmapNames: a.roadmapNames, isImported: a.isImported, inAtlas: true }
                : { ...a, inAtlas: true }
        })

        const orgIds = new Set(filteredOrgActivities.map((a) => a._id))
        const orgKeys = new Set(filteredOrgActivities.map(keyOf).filter((k): k is string => k !== null))
        const dedupedServer = serverResults
            .filter((a) => {
                const k = keyOf(a)
                if (k && orgKeys.has(k)) return false
                return !orgIds.has(a._id)
            })
            .map((a) => ({ ...a, inAtlas: false }))

        const isPlaniswareImported = (a: ActivityResult) => a.source === 'Planisware' && a.isImported
        const externalTop = dedupedServer.filter((a) => !isPlaniswareImported(a))
        const externalBottom = dedupedServer.filter(isPlaniswareImported)

        const filtered =
            page === 1 ? [...orgResults, ...externalTop, ...externalBottom] : [...externalTop, ...externalBottom]

        if (sortBy === 'title_asc') {
            return [...filtered].sort((a, b) => a.title.localeCompare(b.title))
        }
        if (sortBy === 'title_desc') {
            return [...filtered].sort((a, b) => b.title.localeCompare(a.title))
        }
        if (sortBy === 'recently_updated') {
            return [...filtered].sort((a, b) => {
                if (!a.updatedAt && !b.updatedAt) return 0
                if (!a.updatedAt) return 1
                if (!b.updatedAt) return -1
                return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
            })
        }

        return filtered
    }, [hasSearched, searchData, filteredOrgActivities, page, activityStatusDetails, currentRoadmapIds, sortBy])

    // The pager and headline both derive from the server's true total (thousands), so every matching
    // record stays reachable by paging. Org-DB activities surface on page 1 on top of the server page,
    // so they're added to the displayed count.
    const totalServerResults = searchData?.total ?? 0
    const totalPages = Math.max(1, Math.ceil(totalServerResults / rowsPerPage))
    // Only native (non-external) org rows are additive. An org row sourced from an external
    // integration (GSL/Planisware) is already represented in totalServerResults — the search
    // returns it too, and it merges into a single card — so counting it here would double up
    // (e.g. an imported GSL study showing "2 results" for one [GSL][Atlas] card). Unknown/legacy
    // sources fall through to additive, since they aren't in the external search set.
    const additiveOrgCount = filteredOrgActivities.filter((a) => {
        const code = sourceStringToIntegrationCode(a.source ?? 'Atlas')
        return !(code && INTEGRATIONS[code].isExternal)
    }).length
    const totalResultsLabel = hasSearched ? totalServerResults + additiveOrgCount : 0
    const filteredActivities = paginatedActivities

    // External imports (GSL / Planisware) can be re-selected to overwrite their fields with fresh
    // search-result data. Internal activities only create a join row, so re-importing is a no-op.
    const selectableActivities = filteredActivities.filter(
        (a) => !a.isImported || a.source === 'GSL' || a.source === 'Planisware'
    )
    const allSelected =
        selectableActivities.length > 0 && selectableActivities.every((a) => !!selectedActivities[a._id])
    const someSelected = selectableActivities.some((a) => !!selectedActivities[a._id])

    const handleSelect = (id: string, checked: boolean) => {
        const activity = filteredActivities.find((a) => a._id === id)
        setSelectedActivities((prev) => {
            if (!checked) {
                const next = { ...prev }
                delete next[id]
                return next
            }
            if (!activity) return prev
            return { ...prev, [id]: activity }
        })
    }

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const next = { ...selectedActivities }
            selectableActivities.forEach((a) => {
                next[a._id] = a
            })
            setSelectedActivities(next)
        } else {
            setSelectedActivities({})
        }
    }

    const handleClearSelected = () => setSelectedActivities({})

    // Re-importing an external (GSL/Planisware) record that is already on this roadmap overwrites its
    // existing fields. Gate the import behind a confirmation dialog whenever the selection contains such
    // records; brand-new records and internal join rows never trigger it.
    const handleImportSelected = () => {
        const selected = Object.values(selectedActivities)
        const overwrites = selected.filter((a) => (a.source === 'GSL' || a.source === 'Planisware') && a.isImported)
        if (overwrites.length > 0) {
            setOverwriteTitles(overwrites.map((a) => a.title))
            return
        }
        void performImport()
    }

    const performImport = async () => {
        if (!strategy?._id || !strategy?.organizationId) return

        const selected = Object.values(selectedActivities)
        const external = selected.filter((a) => a.source === 'GSL' || a.source === 'Planisware')
        const internal = selected.filter((a) => a.source !== 'GSL' && a.source !== 'Planisware')

        const toStringArray = (v: string | string[] | undefined | null): string[] | undefined => {
            const expand = (s: string): string[] => {
                const trimmed = s.trim()
                if (!trimmed) return []
                if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
                    let parsed: unknown = null
                    try {
                        parsed = JSON.parse(trimmed)
                    } catch (_) {
                        try {
                            parsed = JSON.parse(trimmed.replace(/'/g, '"'))
                        } catch (_) {
                            parsed = null
                        }
                    }
                    if (Array.isArray(parsed)) {
                        return parsed
                            .filter((p): p is string => typeof p === 'string')
                            .map((p) => p.trim())
                            .filter((p) => p.length > 0)
                    }
                }
                return [trimmed]
            }
            if (Array.isArray(v)) {
                const cleaned = v.filter((x): x is string => typeof x === 'string' && x.length > 0).flatMap(expand)
                return cleaned.length ? cleaned : undefined
            }
            if (typeof v === 'string' && v.length > 0) {
                const expanded = expand(v)
                return expanded.length ? expanded : undefined
            }
            return undefined
        }

        await Promise.all([
            external.length > 0 &&
                importExternalActivity({
                    activities: external.map((a) => ({
                        studyNumber: a.studyNumber,
                        source: a.source as 'GSL' | 'Planisware',
                        title: a.title,
                        titleLong: a.titleLong,
                        organizationId: strategy.organizationId,
                        description: a.description,
                        status: normalizeActivityStatus(a.status),
                        startDate: a.startDate ? getISOStringFromCalendarDate(dayjs(a.startDate)) : undefined,
                        endDate: a.endDate ? getISOStringFromCalendarDate(dayjs(a.endDate)) : undefined,
                        plannedStartDate: a.plannedStartDate
                            ? getISOStringFromCalendarDate(dayjs(a.plannedStartDate))
                            : undefined,
                        plannedEndDate: a.plannedEndDate
                            ? getISOStringFromCalendarDate(dayjs(a.plannedEndDate))
                            : undefined,
                        category: a.category,
                        function: a.function,
                        indication: a.indication,
                        therapeuticArea: a.therapeuticArea,
                        compoundName: toStringArray(a.compoundName),
                        genericName: toStringArray(a.genericName),
                        affiliateName: a.affiliateName,
                        providerName: a.providerName,
                        sponsorType: a.sponsorType,
                        countries: toStringArray(a.countries),
                        studyReportDate: a.studyReportDate
                            ? getISOStringFromCalendarDate(dayjs(a.studyReportDate))
                            : undefined,
                        terminationDate: a.terminationDate
                            ? getISOStringFromCalendarDate(dayjs(a.terminationDate))
                            : undefined,
                        studyOwner: a.studyOwner,
                        vendorDetails: a.vendorDetails,
                        vendor: a.vendor,
                        type: a.type,
                        population: a.population,
                        externalId: a.externalId,
                    })),
                    roadmapId: strategy._id,
                }),
            internal.length > 0 &&
                importActivities({
                    activityIds: internal.map((a) => a._id),
                    roadmapId: strategy._id,
                }),
        ])

        await invalidateActivitiesQuery()
        if (external.length > 0) onImportSuccess?.()
        handleClose()
    }

    const hasSelection = Object.keys(selectedActivities).length > 0

    const searchPanel = (
        <Grid container direction="column" spacing={2} wrap="nowrap">
            {/* Generic Name */}
            <Grid item>
                <Typography variant="16x400">Generic Name</Typography>
                <Autocomplete
                    multiple
                    options={facetsData?.genericNames ?? []}
                    value={form.genericNames}
                    onChange={(_, val) => setForm((f) => ({ ...f, genericNames: val }))}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                        ))
                    }
                    renderOption={(props, option) => {
                        const synonyms = getSynonymsFor('genericName', option)
                        return <li {...props}>{synonyms ? `${option} (${synonyms.join(', ')})` : option}</li>
                    }}
                    renderInput={(params) => <TextField {...params} size="small" placeholder="Search Generic Name" />}
                />
            </Grid>

            {/* Indication */}
            <Grid item>
                <Typography variant="16x400">Indication</Typography>
                <Autocomplete
                    multiple
                    options={uniqueIndications}
                    value={form.indications}
                    onChange={(_, val) => setForm((f) => ({ ...f, indications: val }))}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                        ))
                    }
                    renderOption={(props, option) => {
                        const synonyms = getSynonymsFor('indication', option)
                        return <li {...props}>{synonyms ? `${option} (${synonyms.join(', ')})` : option}</li>
                    }}
                    renderInput={(params) => <TextField {...params} size="small" placeholder="Search Indications" />}
                />
            </Grid>

            {/* Compound */}
            <Grid item>
                <Typography variant="16x400">Compound</Typography>
                <Autocomplete
                    multiple
                    options={facetsData?.compoundNames ?? []}
                    value={form.compoundNames}
                    onChange={(_, val) => setForm((f) => ({ ...f, compoundNames: val }))}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                        ))
                    }
                    renderOption={(props, option) => {
                        const synonyms = getSynonymsFor('compoundName', option)
                        return <li {...props}>{synonyms ? `${option} (${synonyms.join(', ')})` : option}</li>
                    }}
                    renderInput={(params) => <TextField {...params} size="small" placeholder="Search Compound" />}
                />
            </Grid>

            {/* Study Type */}
            <Grid item>
                <Typography variant="16x400">Study Type</Typography>
                <Autocomplete
                    multiple
                    options={facetsData?.studyTypes ?? []}
                    value={form.studyTypes}
                    onChange={(_, val) => setForm((f) => ({ ...f, studyTypes: val }))}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                        ))
                    }
                    renderOption={(props, option) => {
                        const synonyms = getSynonymsFor('studyType', option)
                        return <li {...props}>{synonyms ? `${option} (${synonyms.join(', ')})` : option}</li>
                    }}
                    renderInput={(params) => <TextField {...params} size="small" placeholder="Search Study Types" />}
                />
            </Grid>

            {/* Source chips */}
            <Grid item>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {availableIntegrations.map((integration) => {
                        const selected = form.sources.includes(integration.code)
                        const onToggle = () =>
                            setForm((f) => ({
                                ...f,
                                sources: selected
                                    ? f.sources.filter((s) => s !== integration.code)
                                    : [...f.sources, integration.code],
                            }))
                        return selected ? (
                            <Chip
                                key={integration.code}
                                label={integration.label}
                                size="small"
                                onClick={onToggle}
                                sx={{ ...SOURCE_CHIP_SX(integration.chipColor), cursor: 'pointer' }}
                            />
                        ) : (
                            <Chip
                                key={integration.code}
                                label={integration.label}
                                size="small"
                                variant="outlined"
                                onClick={onToggle}
                                sx={{
                                    borderRadius: '12px',
                                    borderColor: integration.chipColor,
                                    backgroundColor: 'transparent',
                                    '& .MuiChip-label': { opacity: 1 },
                                    cursor: 'pointer',
                                }}
                            />
                        )
                    })}
                </Box>
            </Grid>

            {/* Advanced Search */}
            <Grid item>
                <Accordion disableGutters elevation={0}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: 'inherit' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Advanced Search
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container direction="column" spacing={2}>
                            {/* Status multi-select */}
                            <Grid item>
                                <Typography variant="16x400">Status (multi-select)</Typography>
                                <FormControl variant="outlined" fullWidth size="small">
                                    <Select
                                        displayEmpty
                                        multiple
                                        value={form.statuses}
                                        onChange={(e) =>
                                            setForm((f) => ({ ...f, statuses: e.target.value as string[] }))
                                        }
                                        input={<OutlinedInput />}
                                        renderValue={(selected) => {
                                            if (selected.length === 0) return '' as any
                                            if (allStatusIds.length > 0 && selected.length === allStatusIds.length) {
                                                return 'All' as any
                                            }
                                            return selected
                                                .map((id) => activityStatusDetails.find((s) => s.id === id)?.name ?? id)
                                                .join(', ') as any
                                        }}
                                        MenuProps={{
                                            disableScrollLock: true,
                                            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                            transformOrigin: { vertical: 'top', horizontal: 'left' },
                                        }}
                                        sx={{ borderRadius: '12px' }}
                                    >
                                        {activityStatusDetails.map((status) => (
                                            <MenuItem key={status.id} value={status.id}>
                                                <Checkbox checked={form.statuses.includes(status.id)} />
                                                <ListItemText primary={status.name} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Therapeutic Area */}
                            <Grid item>
                                <Typography variant="16x400">Therapeutic Area</Typography>
                                <Autocomplete
                                    options={facetsData?.therapeuticAreas ?? []}
                                    value={form.therapeuticArea || null}
                                    onChange={(_, val) => setForm((f) => ({ ...f, therapeuticArea: val ?? '' }))}
                                    renderOption={(props, option) => {
                                        const synonyms = getSynonymsFor('therapeuticArea', option)
                                        return (
                                            <li {...props}>
                                                {synonyms ? `${option} (${synonyms.join(', ')})` : option}
                                            </li>
                                        )
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} size="small" placeholder="Search Therapeutic Area" />
                                    )}
                                />
                            </Grid>

                            <Grid item>
                                <Typography variant="16x400">Study Title</Typography>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    value={form.studyTitle}
                                    onChange={(e) => setForm((f) => ({ ...f, studyTitle: e.target.value }))}
                                    placeholder="Enter Study Title"
                                />
                            </Grid>

                            <Grid item>
                                <Typography variant="16x400">Study Number</Typography>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    value={form.studyId}
                                    onChange={(e) => setForm((f) => ({ ...f, studyId: e.target.value }))}
                                    placeholder="Enter Study Number"
                                />
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </Grid>

            {/* Hide already imported — a search modifier; only applied on the next Search click */}
            <Grid item>
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            checked={form.hideImported}
                            onChange={(e) => setForm((f) => ({ ...f, hideImported: e.target.checked }))}
                        />
                    }
                    label={
                        <Typography variant="body2" sx={{ color: 'figma.grayscale.tier2' }}>
                            Hide already imported
                        </Typography>
                    }
                />
            </Grid>

            {/* Search button */}
            <Grid item>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleSearch}
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
                    sx={{ py: 1.5, textTransform: 'uppercase', fontWeight: 700, borderRadius: '14px' }}
                >
                    {isLoading ? 'Loading…' : 'Search'}
                </Button>
            </Grid>
        </Grid>
    )

    const resultsPanel = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Fixed header — never scrolls away */}
            <Box sx={{ flexShrink: 0 }}>
                {/* Sort controls */}
                {hasSearched && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                        >
                            Sort:
                        </Typography>
                        <Select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.75rem', height: 28, minWidth: 160 }}
                            MenuProps={{ disableScrollLock: true }}
                        >
                            <MenuItem value="default">Default</MenuItem>
                            <MenuItem value="title_asc">Title A → Z</MenuItem>
                            <MenuItem value="title_desc">Title Z → A</MenuItem>
                            <MenuItem value="recently_updated">Recently Updated</MenuItem>
                        </Select>
                    </Box>
                )}

                {/* Pagination + results count */}
                {filteredActivities.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 1,
                            // borderBottom: '1px solid',
                            // borderColor: 'divider',
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            {isLoading ? '—' : `${totalResultsLabel} results`}
                        </Typography>

                        {!isLoading && (
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, value) => setPage(value)}
                                size="small"
                                color="primary"
                            />
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Per page:
                            </Typography>
                            <Select
                                value={rowsPerPage}
                                onChange={(e) => {
                                    setRowsPerPage(Number(e.target.value))
                                    setPage(1)
                                }}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.8rem', minWidth: 60, height: 28 }}
                            >
                                {ROWS_PER_PAGE_OPTIONS.map((n) => (
                                    <MenuItem key={n} value={n}>
                                        {n}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    </Box>
                )}

                {/* Select all row */}
                {filteredActivities.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={allSelected}
                                    indeterminate={someSelected && !allSelected}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                    size="small"
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'figma.primary' }}>
                                    SELECT ALL
                                </Typography>
                            }
                            labelPlacement="start"
                            sx={{ mr: 0 }}
                        />
                    </Box>
                )}
            </Box>

            {/* Scrolling body */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0, position: 'relative' }}>
                {hasSearched && searchError ? (
                    <EmptyPlaceholder
                        title="Search failed"
                        subtitle="Could not reach the external activity service. Check your connection and try again."
                    />
                ) : isLoading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            gap: 2,
                        }}
                    >
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary">
                            Loading activities…
                        </Typography>
                    </Box>
                ) : filteredActivities.length > 0 ? (
                    paginatedActivities.map((activity) => (
                        <ActivityResultCard
                            key={activity._id}
                            activity={activity}
                            selected={!!selectedActivities[activity._id]}
                            onSelect={handleSelect}
                        />
                    ))
                ) : (
                    <EmptyPlaceholder
                        title={hasSearched ? 'No results found' : 'Run a search to see results'}
                        subtitle={hasSearched ? 'Try adjusting your search criteria' : undefined}
                    />
                )}

                {/* Floating import bar */}
                {hasSelection && (
                    <Box
                        sx={{
                            position: 'sticky',
                            bottom: 0,
                            display: 'inline-flex',
                            p: '12px 16px',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: '16px',
                            borderRadius: '16px',
                            backgroundColor: 'figma.grayscale.tier7',
                            boxShadow: '0 8px 16px 0 rgba(0, 0, 0, 0.17)',
                            float: 'right',
                            zIndex: 10,
                        }}
                    >
                        <Button onClick={handleClearSelected} variant="outlined" color="primary" size="small">
                            Clear Selected
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={handleImportSelected}
                            disabled={isImporting}
                            startIcon={isImporting ? <CircularProgress size={14} color="inherit" /> : undefined}
                        >
                            {isImporting ? 'Importing…' : `Import (${Object.keys(selectedActivities).length})`}
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    )

    return (
        <>
            {trigger ? (
                trigger(handleOpen)
            ) : (
                <Tooltip title={isSandbox ? "Importing isn't available on a sandbox roadmap" : ''}>
                    <span>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleOpen}
                            disabled={disabled || isSandbox}
                            style={{ width: 140, height: 52, borderRadius: '14px' }}
                        >
                            Import
                        </Button>
                    </span>
                </Tooltip>
            )}

            <SearchModal
                open={open}
                onClose={handleClose}
                title="Importing Evidence Tactics"
                leftPanel={searchPanel}
                leftPanelWidth={300}
                leftPanelRef={leftPanelRef}
            >
                {resultsPanel}
            </SearchModal>

            <OverwriteConfirmDialog
                open={overwriteTitles.length > 0}
                recordTitles={overwriteTitles}
                isLoading={isImporting}
                onClose={() => setOverwriteTitles([])}
                onConfirm={async () => {
                    setOverwriteTitles([])
                    await performImport()
                }}
            />
        </>
    )
}

export default ActivitySearchModal
